import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import type { ComponentContentSchema } from '@vibesites/runtime';
import { useCollectionBinding } from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';
import {
  GalleryLightbox,
  GallerySharedStyles,
  type GalleryImage,
} from './_galleryShared';

/**
 * The property photo gallery — a single, unified masonry grid that shows
 * every photo in the property `photo_gallery` collection, apartment and
 * community shots alike. The collection binding has no `title` filter, so
 * uncategorized photos surface here too (previously they were split across
 * separate "Apartment" and "Community" sections joined by a separator band).
 *
 * The slot is still named `GalleryApartments` for backwards compatibility
 * with existing pages and saved content, but it now renders the whole gallery.
 * Clicking any tile opens the shared lightbox at that photo.
 */

/**
 * Number of masonry columns for the current viewport (3 desktop / 2 tablet /
 * 1 mobile). SSG renders the desktop default; the effect corrects it on mount
 * and on resize. Kept as a hook so the distribution memo can react to it.
 */
function useMasonryColumns(): number {
  const [columns, setColumns] = useState(3);

  useEffect(() => {
    const compute = () => {
      const w = window.innerWidth;
      setColumns(w < 640 ? 1 : w < 1024 ? 2 : 3);
    };
    compute();
    window.addEventListener('resize', compute);
    return () => window.removeEventListener('resize', compute);
  }, []);

  return columns;
}

interface Tile {
  img: GalleryImage;
  index: number;
}

/**
 * Mobile-only swipeable slideshow: one photo at a time on a horizontally
 * translated track. Swiping left/right (or tapping the arrows / dots)
 * paginates; tapping a photo without swiping opens the shared lightbox at
 * that index. Renders in place of the masonry grid below 640px.
 */
function GallerySlideshow({
  images,
  onOpen,
}: {
  images: GalleryImage[];
  onOpen: (index: number) => void;
}) {
  const total = images.length;
  const [current, setCurrent] = useState(0);

  const goTo = useCallback(
    (index: number) => setCurrent(((index % total) + total) % total),
    [total],
  );
  const goNext = useCallback(() => goTo(current + 1), [goTo, current]);
  const goPrev = useCallback(() => goTo(current - 1), [goTo, current]);

  // Touch / swipe state. `didSwipe` lets the tap-to-open handler ignore the
  // synthetic click that fires at the end of a swipe gesture.
  const touchStartX = useRef<number | null>(null);
  const touchStartY = useRef<number | null>(null);
  const didSwipe = useRef(false);

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
    touchStartY.current = e.touches[0].clientY;
    didSwipe.current = false;
  }, []);

  const handleTouchEnd = useCallback(
    (e: React.TouchEvent) => {
      if (touchStartX.current === null) return;
      const dx = e.changedTouches[0].clientX - touchStartX.current;
      const dy =
        touchStartY.current === null ? 0 : e.changedTouches[0].clientY - touchStartY.current;
      touchStartX.current = null;
      touchStartY.current = null;
      // Require mostly-horizontal movement so vertical scroll doesn't paginate.
      if (Math.abs(dx) < 30 || Math.abs(dx) < Math.abs(dy)) return;
      didSwipe.current = true;
      if (dx < 0) goNext();
      else goPrev();
    },
    [goNext, goPrev],
  );

  return (
    <div className="gallery-slideshow">
      <div
        className="gallery-slideshow__viewport"
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        <div
          className="gallery-slideshow__track"
          style={{ transform: `translateX(-${current * 100}%)` }}
        >
          {images.map((img, index) => (
            <button
              type="button"
              key={img.image || index}
              className="gallery-slideshow__slide gallery-item group"
              // Ignore the click synthesized right after a swipe-release.
              onClick={() => {
                if (didSwipe.current) {
                  didSwipe.current = false;
                  return;
                }
                onOpen(index);
              }}
              aria-label={`View photo ${index + 1}`}
              aria-hidden={index !== current}
              tabIndex={index === current ? 0 : -1}
            >
              <VibesitesImage
                url={img.image}
                alt={img.alt_text || img.title || ''}
                sizes="100vw"
                className="w-full h-full object-cover"
              />
            </button>
          ))}
        </div>
      </div>

      {total > 1 && (
        <>
          <button
            type="button"
            onClick={goPrev}
            className="gallery-slideshow__arrow gallery-slideshow__arrow--prev"
            aria-label="Previous photo"
          >
            <ChevronLeft aria-hidden="true" size={28} strokeWidth={2.25} />
          </button>
          <button
            type="button"
            onClick={goNext}
            className="gallery-slideshow__arrow gallery-slideshow__arrow--next"
            aria-label="Next photo"
          >
            <ChevronRight aria-hidden="true" size={28} strokeWidth={2.25} />
          </button>

          <div className="gallery-slideshow__dots" role="tablist" aria-label="Choose photo">
            {images.map((img, index) => (
              <button
                type="button"
                key={img.image || index}
                className={`gallery-slideshow__dot ${
                  index === current ? 'gallery-slideshow__dot--active' : ''
                }`}
                onClick={() => goTo(index)}
                aria-label={`Go to photo ${index + 1}`}
                aria-selected={index === current}
                role="tab"
              />
            ))}
          </div>

          <div className="gallery-slideshow__counter" aria-live="polite">
            {current + 1} / {total}
          </div>
        </>
      )}
    </div>
  );
}

export function GalleryApartments() {
  const images = useCollectionBinding<GalleryImage>('galleryapartments.images', {
    collection: 'photo_gallery',
    defaultLimit: 48,
  });

  const columnCount = useMasonryColumns();
  // Below 640px (single-column viewport) we swap the masonry grid for a
  // swipeable slideshow; tablet/desktop keep the masonry.
  const isMobile = columnCount === 1;

  // Round-robin distribution so photos read left-to-right by row (photo 0 in
  // the first column, 1 in the second, 2 in the third, 3 back in the first…),
  // matching a shortest-column masonry's visual order while staying pure CSS.
  const columns = useMemo<Tile[][]>(() => {
    const buckets: Tile[][] = Array.from({ length: columnCount }, () => []);
    images.forEach((img, index) => {
      buckets[index % columnCount].push({ img, index });
    });
    return buckets;
  }, [images, columnCount]);

  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);

  if (images.length === 0) {
    return (
      <section className="bg-white">
        <div className="gallery-constrained py-12 text-center text-[var(--vibe-color-text-muted)]">
          <p className="font-body text-lg">Gallery images will be displayed here.</p>
        </div>
        <GallerySharedStyles />
      </section>
    );
  }

  return (
    <section className="bg-white">
      <div className="gallery-constrained pt-8 md:pt-12 pb-12">
        {isMobile ? (
          <GallerySlideshow images={images} onOpen={setSelectedIndex} />
        ) : (
        <div className="gallery-masonry">
          {columns.map((col, colIndex) => (
            <div className="gallery-masonry__col" key={colIndex}>
              {col.map(({ img, index }, rowIndex) => {
                // Checkerboard stagger: landscape (4:3) on even cells, vertical
                // (3:4) on odd cells, so rows read L-V-L then flip to V-L-V.
                const isVertical = (rowIndex + colIndex) % 2 === 1;
                return (
                  <button
                    type="button"
                    key={img.image || index}
                    className={`gallery-masonry__item gallery-item group ${
                      isVertical
                        ? 'gallery-masonry__item--vertical'
                        : 'gallery-masonry__item--landscape'
                    }`}
                    onClick={() => setSelectedIndex(index)}
                    aria-label={`View photo ${index + 1}`}
                  >
                    <VibesitesImage
                      url={img.image}
                      alt={img.alt_text || img.title || ''}
                      sizes="(min-width: 1024px) 33vw, (min-width: 640px) 50vw, 100vw"
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                  </button>
                );
              })}
            </div>
          ))}
        </div>
        )}
      </div>

      <GalleryLightbox
        images={images}
        selectedIndex={selectedIndex}
        onClose={() => setSelectedIndex(null)}
        onChange={setSelectedIndex}
      />

      <GallerySharedStyles />

      <style>{`
        /* Row-order masonry: N side-by-side flex columns, each stacking its
           tiles at their natural aspect ratio. */
        .gallery-masonry {
          display: flex;
          align-items: flex-start;
          gap: 0.5rem;
        }
        .gallery-masonry__col {
          display: flex;
          flex: 1 1 0;
          min-width: 0;
          flex-direction: column;
          gap: 0.5rem;
        }
        .gallery-masonry__item {
          display: block;
          width: 100%;
          margin: 0;
          padding: 0;
          border: none;
          background: none;
          border-radius: 2px;
          overflow: hidden;
          cursor: pointer;
          line-height: 0;
        }
        /* Fixed-aspect tiles produce the staggered L/V checkerboard; images are
           cropped to fill via object-cover. */
        .gallery-masonry__item--landscape { aspect-ratio: 4 / 3; }
        .gallery-masonry__item--vertical { aspect-ratio: 3 / 4; }
        .gallery-masonry__item img {
          display: block;
          width: 100%;
          height: 100%;
        }

        /* ── Mobile slideshow ──────────────────────────────────────
           One photo per viewport on a horizontally sliding track.
           touch-action: pan-y lets vertical page scroll through while we
           handle horizontal swipes ourselves. */
        .gallery-slideshow {
          position: relative;
          width: 100%;
        }
        .gallery-slideshow__viewport {
          overflow: hidden;
          width: 100%;
          border-radius: 2px;
          touch-action: pan-y;
        }
        .gallery-slideshow__track {
          display: flex;
          transition: transform 0.35s ease;
          will-change: transform;
        }
        .gallery-slideshow__slide {
          flex: 0 0 100%;
          width: 100%;
          aspect-ratio: 4 / 3;
          margin: 0;
          padding: 0;
          border: none;
          background: none;
          overflow: hidden;
          cursor: pointer;
          line-height: 0;
        }
        .gallery-slideshow__slide img {
          display: block;
          width: 100%;
          height: 100%;
        }
        .gallery-slideshow__arrow {
          position: absolute;
          top: 50%;
          transform: translateY(-50%);
          width: 2.75rem;
          height: 2.75rem;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #fff;
          background: rgba(0, 0, 0, 0.4);
          border: none;
          cursor: pointer;
          padding: 0;
          border-radius: 9999px;
          z-index: 2;
          -webkit-user-select: none;
          user-select: none;
        }
        .gallery-slideshow__arrow--prev { left: 0.5rem; }
        .gallery-slideshow__arrow--next { right: 0.5rem; }
        .gallery-slideshow__dots {
          display: flex;
          justify-content: center;
          gap: 0.5rem;
          margin-top: 0.75rem;
        }
        .gallery-slideshow__dot {
          width: 0.5rem;
          height: 0.5rem;
          padding: 0;
          border: none;
          border-radius: 9999px;
          background: rgba(0, 0, 0, 0.25);
          cursor: pointer;
          transition: background 0.15s, transform 0.15s;
        }
        .gallery-slideshow__dot--active {
          background: var(--vibe-color-primary);
          transform: scale(1.25);
        }
        .gallery-slideshow__counter {
          text-align: center;
          margin-top: 0.5rem;
          color: var(--vibe-color-text-muted);
          font-size: 0.8125rem;
          letter-spacing: 0.05em;
        }

        @media (prefers-reduced-motion: reduce) {
          .gallery-slideshow__track { transition: none; }
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'GalleryApartments',
  displayName: 'Photo Gallery',
  tokens: {},
  collectionBindings: {
    'galleryapartments.images': {
      label: 'Gallery Photos',
      collection: 'photo_gallery',
      defaultLimit: 48,
      description:
        'Every photo from the property photo gallery — both apartment and community shots — shown in one masonry grid.',
    },
  },
};

(GalleryApartments as unknown as { contentSchema: ComponentContentSchema }).contentSchema =
  contentSchema;

export default GalleryApartments;
